import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { registerSW } from 'virtual:pwa-register';

/**
 * Non-intrusive update prompt.
 *
 * The service worker is registered with `prompt` rather than `autoUpdate`
 * because reloading while someone is halfway through editing a class would
 * discard their input.
 *
 * "Later" only defers within the session: a waiting worker is promoted by the
 * browser itself once the app is fully closed, so the next launch opens the
 * new version with no prompt. That is inherent service worker lifecycle (and
 * the desired outcome — after a restart there is no in-progress input to
 * protect), which is why the banner says so.
 */
export function UpdatePrompt(): JSX.Element | null {
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [update, setUpdate] = useState<(() => Promise<void>) | null>(null);

  useEffect(() => {
    const updateSW = registerSW({
      onNeedRefresh: () => setNeedsRefresh(true),
    });
    setUpdate(() => async () => {
      await updateSW(true);
    });
  }, []);

  if (needsRefresh) {
    return (
      <div class="update-banner" role="status">
        <span>A new version is ready. Later applies it when you next open the app.</span>
        <button type="button" class="primary" onClick={() => void update?.()}>
          Update now
        </button>
        <button type="button" onClick={() => setNeedsRefresh(false)}>
          Later
        </button>
      </div>
    );
  }

  return null;
}
